"use client";

import { useCallback, useSyncExternalStore } from "react";

import type { CustomerFavouritesOwner } from "@/app/favourites/domain/customerFavouritesContracts";
import { buildCustomerFavouritesOwnerKey } from "@/app/favourites/customerFavouritesStore";

const LOCATION_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/u;

export type CustomerFavouriteOperationIntent = "add" | "remove";

export interface CustomerFavouriteOperationToken {
  readonly generation: number;
  readonly ownerKey: string;
  readonly customerAccountId: string;
  readonly locationId: string;
  readonly intent: CustomerFavouriteOperationIntent;
  readonly targetKey: string;
}

export type CustomerFavouriteOperationStartResult =
  | {
      status: "started";
      token: CustomerFavouriteOperationToken;
    }
  | {
      status: "blocked";
      reason:
        | "operation-pending"
        | "invalid-owner"
        | "invalid-location-id"
        | "invalid-intent";
      activeIntent: CustomerFavouriteOperationIntent | null;
    };

export type CustomerFavouriteOperationState =
  | { status: "idle" }
  | {
      status: "pending";
      intent: CustomerFavouriteOperationIntent;
      generation: number;
    };

export interface CustomerFavouriteOperationCoordinator {
  begin(
    owner: CustomerFavouritesOwner,
    locationId: string,
    intent: CustomerFavouriteOperationIntent,
  ): CustomerFavouriteOperationStartResult;
  finish(token: CustomerFavouriteOperationToken): boolean;
  invalidateTarget(
    owner: CustomerFavouritesOwner,
    locationId: string,
  ): boolean;
  invalidateOwner(owner: CustomerFavouritesOwner): number;
  clearCustomer(customerAccountId: string): number;
  isCurrent(token: CustomerFavouriteOperationToken): boolean;
  getState(
    owner: CustomerFavouritesOwner,
    locationId: string,
  ): CustomerFavouriteOperationState;
  subscribe(listener: () => void): () => void;
}

const IDLE_STATE = Object.freeze({
  status: "idle",
} satisfies CustomerFavouriteOperationState);

function buildTargetKey(customerAccountId: string, locationId: string) {
  return JSON.stringify([customerAccountId, locationId]);
}

function isLocationId(value: unknown): value is string {
  return typeof value === "string" && LOCATION_ID_PATTERN.test(value);
}

export function createCustomerFavouriteOperationCoordinator(): CustomerFavouriteOperationCoordinator {
  let nextGeneration = 0;
  const activeByTarget = new Map<
    string,
    CustomerFavouriteOperationToken
  >();
  const listeners = new Set<() => void>();
  const pendingStateCache = new Map<
    string,
    { generation: number; state: CustomerFavouriteOperationState }
  >();

  const publish = () => {
    for (const listener of [...listeners]) {
      listener();
    }
  };

  const begin: CustomerFavouriteOperationCoordinator["begin"] = (
    owner,
    locationId,
    intent,
  ) => {
    const ownerKey = buildCustomerFavouritesOwnerKey(owner);

    if (!ownerKey) {
      return {
        status: "blocked",
        reason: "invalid-owner",
        activeIntent: null,
      };
    }

    if (!isLocationId(locationId)) {
      return {
        status: "blocked",
        reason: "invalid-location-id",
        activeIntent: null,
      };
    }

    if (intent !== "add" && intent !== "remove") {
      return {
        status: "blocked",
        reason: "invalid-intent",
        activeIntent: null,
      };
    }

    const targetKey = buildTargetKey(owner.customerAccountId, locationId);
    const active = activeByTarget.get(targetKey);

    if (active) {
      return {
        status: "blocked",
        reason: "operation-pending",
        activeIntent: active.intent,
      };
    }

    const token = Object.freeze({
      generation: ++nextGeneration,
      ownerKey,
      customerAccountId: owner.customerAccountId,
      locationId,
      intent,
      targetKey,
    } satisfies CustomerFavouriteOperationToken);

    activeByTarget.set(targetKey, token);
    pendingStateCache.delete(targetKey);
    publish();

    return { status: "started", token };
  };

  const isCurrent: CustomerFavouriteOperationCoordinator["isCurrent"] = (
    token,
  ) => activeByTarget.get(token.targetKey) === token;

  const finish: CustomerFavouriteOperationCoordinator["finish"] = (
    token,
  ) => {
    if (!isCurrent(token)) {
      return false;
    }

    activeByTarget.delete(token.targetKey);
    pendingStateCache.delete(token.targetKey);
    publish();
    return true;
  };

  const invalidateTarget: CustomerFavouriteOperationCoordinator["invalidateTarget"] =
    (owner, locationId) => {
      const ownerKey = buildCustomerFavouritesOwnerKey(owner);

      if (!ownerKey || !isLocationId(locationId)) {
        return false;
      }

      const targetKey = buildTargetKey(owner.customerAccountId, locationId);

      if (!activeByTarget.delete(targetKey)) {
        return false;
      }

      pendingStateCache.delete(targetKey);
      publish();
      return true;
    };

  const invalidateOwner: CustomerFavouriteOperationCoordinator["invalidateOwner"] =
    (owner) => {
      const ownerKey = buildCustomerFavouritesOwnerKey(owner);

      if (!ownerKey) {
        return 0;
      }

      let removedCount = 0;

      for (const [targetKey, token] of activeByTarget) {
        if (token.ownerKey !== ownerKey) {
          continue;
        }

        activeByTarget.delete(targetKey);
        pendingStateCache.delete(targetKey);
        removedCount += 1;
      }

      if (removedCount > 0) {
        publish();
      }

      return removedCount;
    };

  const clearCustomer: CustomerFavouriteOperationCoordinator["clearCustomer"] =
    (customerAccountId) => {
      let removedCount = 0;

      for (const [targetKey, token] of activeByTarget) {
        if (token.customerAccountId !== customerAccountId) {
          continue;
        }

        activeByTarget.delete(targetKey);
        pendingStateCache.delete(targetKey);
        removedCount += 1;
      }

      if (removedCount > 0) {
        publish();
      }

      return removedCount;
    };

  const getState: CustomerFavouriteOperationCoordinator["getState"] = (
    owner,
    locationId,
  ) => {
    const ownerKey = buildCustomerFavouritesOwnerKey(owner);

    if (!ownerKey || !isLocationId(locationId)) {
      return IDLE_STATE;
    }

    const targetKey = buildTargetKey(owner.customerAccountId, locationId);
    const active = activeByTarget.get(targetKey);

    if (!active) {
      return IDLE_STATE;
    }

    const cached = pendingStateCache.get(targetKey);

    if (cached?.generation === active.generation) {
      return cached.state;
    }

    const state = Object.freeze({
      status: "pending",
      intent: active.intent,
      generation: active.generation,
    } satisfies CustomerFavouriteOperationState);
    pendingStateCache.set(targetKey, {
      generation: active.generation,
      state,
    });
    return state;
  };

  return Object.freeze({
    begin,
    finish,
    invalidateTarget,
    invalidateOwner,
    clearCustomer,
    isCurrent,
    getState,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  });
}

export const customerFavouriteOperationCoordinator =
  createCustomerFavouriteOperationCoordinator();

export function useCustomerFavouriteOperationState(
  owner: CustomerFavouritesOwner,
  locationId: string,
  coordinator: CustomerFavouriteOperationCoordinator =
    customerFavouriteOperationCoordinator,
) {
  const subscribe = useCallback(
    (onStoreChange: () => void) => coordinator.subscribe(onStoreChange),
    [coordinator],
  );
  const getSnapshot = useCallback(
    () => coordinator.getState(owner, locationId),
    [coordinator, locationId, owner],
  );

  return useSyncExternalStore(subscribe, getSnapshot, () => IDLE_STATE);
}
